import { createSlice } from "@reduxjs/toolkit";

// define initial state
const initialState = {
    sidebar: {
        open: false,
        type: "CONTACT",// can be CONTACT, STARRED,SHARED, PATIENT_INFO, FORWARD_MESSAGE, GROUP_MEMBERS
    },
    selectedChatId: null, // Add selectedChatId to initial state
    conversationUser: null, // Store current conversation user info { name, senderId (callingId) }
    chatParams: null, // Store chat parameters { receiverId, contact_name, senderId, sender_name, chat_type, isNonAppUser }
    chatHistory: [], // Store chat history messages
    currentRawChatData: null, // New: Store the full raw chat object from API response
    isVoiceRecorderActive: false, // New: State to control voice recorder visibility
    pagerModalOpen: false, // New: State for pager modal visibility
    currentPagerData: null, // New: State to store data for the pager modal
    chatListTrigger: 0, // New: Trigger to refresh the chat list
    pagerListTrigger: 0, // New: Trigger to refresh the pager list
    allGroupChats: [], // New: Store all raw group chats from /getallchats
    currentPatientDataForChat: null, // New: Store patient data when initiating chat from modal
    messageToEdit: null, // New: Stores the message object being edited
    messageToDelete: null, // New: Stores the message object being deleted
    messageToForward: null, // New: Stores the message object being forwarded
    currentGroupMembers: [], // New: Stores the list of members for the currently active group chat
    isChatStarting: false, // New: State to control the full-screen loader for chat initiation
    isNonAppChatEnded: false, // New: State to control the visibility of the cross button for non-app chats
    isFirstMessageSentToNonAppUser: true, // New: State to track if the first message has been sent to a non-app user
    messageBadgeCount: 0, // New: Badge count for new messages
    pagerBadgeCount: 0, // New: Badge count for new pagers
}

// create slice
const slice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        //Toggle sidebar
        toggleSidebar(state, action) {
            state.sidebar.open = !state.sidebar.open
        },
        updateSidebarType(state, action) {
            state.sidebar.type = action.payload.type;
        },
        setSelectedChatId(state, action) { // Add reducer to set selected chat ID
            state.selectedChatId = action.payload.chatId;
        },
        setConversationUser(state, action) { // Add reducer to set conversation user
            state.conversationUser = action.payload.user;
        },
        setChatParams(state, action) { // Add reducer to set chat parameters
            state.chatParams = action.payload.params;
        },
        setChatHistory(state, action) { // Add reducer to set chat history
            state.chatHistory = action.payload.history;
        },
        setCurrentRawChatData(state, action) { // New: Reducer to set raw chat data
            state.currentRawChatData = action.payload.rawChatData;
        },
        setIsVoiceRecorderActive(state, action) { // New: Reducer to set voice recorder active status
            state.isVoiceRecorderActive = action.payload.isActive;
        },
        setPagerModalOpen(state, action) { // New: Reducer to control pager modal visibility
            state.pagerModalOpen = action.payload.open;
        },
        setCurrentPagerData(state, action) { // New: Reducer to set current pager data
            state.currentPagerData = action.payload.data;
        },
        setChatListTrigger(state) { // New: Reducer to increment chat list trigger
            state.chatListTrigger += 1;
        },
        setPagerListTrigger(state) { // New: Reducer to increment pager list trigger
            state.pagerListTrigger += 1;
        },
        setAllGroupChats(state, action) { // New: Reducer to set all raw group chats
            state.allGroupChats = action.payload.groupChats;
        },
        setCurrentPatientDataForChat(state, action) { // New: Reducer to set current patient data for chat
            state.currentPatientDataForChat = action.payload; // Directly assign the payload
        },
        setMessageToEdit(state, action) { // New: Reducer to set message for editing
            state.messageToEdit = action.payload; // Directly assign the message object
        },
        clearMessageToEdit(state) { // New: Reducer to clear message editing state
            state.messageToEdit = null;
        },
        setMessageToDelete(state, action) { // New: Reducer to set message for deletion
            state.messageToDelete = action.payload;
        },
        clearMessageToDelete(state) { // New: Reducer to clear message deletion state
            state.messageToDelete = null;
        },
        setMessageToForward(state, action) { // New: Reducer to set message for forwarding
            state.messageToForward = action.payload;
            state.sidebar.open = true; // Open sidebar when message is set for forwarding
            state.sidebar.type = 'FORWARD_MESSAGE'; // Set sidebar type
        },
        clearMessageToForward(state) { // New: Reducer to clear message forwarding state
            state.messageToForward = null;
            // Do not close sidebar here, let the sidebar component handle its own closure
        },
        setCurrentGroupMembers(state, action) { // New: Reducer to set current group members
            const members = action.payload;
            if (Array.isArray(members)) {
                const uniqueMembers = [];
                const seenIds = new Set();

                members.forEach(member => {
                    // Prefer member_id for uniqueness, fallback to member_name
                    const uniqueKey = member.member_id || member.id || member.member_name;

                    if (uniqueKey) {
                        if (!seenIds.has(uniqueKey)) {
                            seenIds.add(uniqueKey);
                            uniqueMembers.push(member);
                        }
                    } else {
                        uniqueMembers.push(member);
                    }
                });
                state.currentGroupMembers = uniqueMembers;
            } else {
                state.currentGroupMembers = [];
            }
        },
        setIsChatStarting(state, action) { // New: Reducer to control full-screen loader
            state.isChatStarting = action.payload;
        },
        setNonAppChatEnded(state, action) { // New: Reducer to set non-app chat ended status
            state.isNonAppChatEnded = action.payload;
        },
        setIsFirstMessageSentToNonAppUser(state, action) { // New: Reducer to set first message sent status
            state.isFirstMessageSentToNonAppUser = action.payload;
        },
        incrementMessageBadge(state) {
            state.messageBadgeCount += 1;
        },
        resetMessageBadge(state) {
            state.messageBadgeCount = 0;
        },
        incrementPagerBadge(state) {
            state.pagerBadgeCount += 1;
        },
        resetPagerBadge(state) {
            state.pagerBadgeCount = 0;
        },
        resetChatState(state) { // New: Resets all chat-related states
            state.selectedChatId = null;
            state.conversationUser = null;
            state.chatParams = null;
            state.chatHistory = [];
            state.currentRawChatData = null;
            state.isVoiceRecorderActive = false;
            state.messageToEdit = null;
            state.messageToDelete = null;
            state.messageToForward = null;
            state.currentGroupMembers = [];
            state.isNonAppChatEnded = false;
            state.isFirstMessageSentToNonAppUser = true;
        }
    }
});

// export reducer
export default slice.reducer;

//thunk functions - perform async operations
export function ToggleSidebar() {
    return async (dispatch) => { // dispatch is now an argument
        dispatch(slice.actions.toggleSidebar());
    }
}

export function UpdateSidebarType(type) {
    return async (dispatch) => { // dispatch is now an argument
        dispatch(slice.actions.updateSidebarType({
            type
        }))
    }
}

export function SetSelectedChatId(chatId) { // Action creator for setting selected chat ID
    return async (dispatch) => { // dispatch is now an argument
        dispatch(slice.actions.setSelectedChatId({
            chatId
        }))
    }
}

export function SetConversationUser(user) { // Action creator for setting conversation user
    return async (dispatch) => { // dispatch is now an argument
        dispatch(slice.actions.setConversationUser({
            user
        }))
    }
}

export function SetChatParams(params) { // Action creator for setting chat parameters
    return async (dispatch) => { // dispatch is now an argument
        dispatch(slice.actions.setChatParams({
            params
        }))
    }
}

export function SetChatHistory(history) { // Action creator for setting chat history
    return async (dispatch) => { // dispatch is now an argument
        dispatch(slice.actions.setChatHistory({
            history
        }))
    }
}

export function SetCurrentRawChatData(rawChatData) { // New: Action creator for setting raw chat data
    return async (dispatch) => { // dispatch is now an argument
        dispatch(slice.actions.setCurrentRawChatData({
            rawChatData
        }))
    }
}

export function SetIsVoiceRecorderActive(isActive) { // New: Action creator for setting voice recorder active status
    return async (dispatch) => { // dispatch is now an argument
        dispatch(slice.actions.setIsVoiceRecorderActive({
            isActive
        }))
    }
}

export function SetPagerModalOpen(open) { // New: Action creator for setting pager modal visibility
    return async (dispatch) => { // dispatch is now an argument
        dispatch(slice.actions.setPagerModalOpen({
            open
        }))
    }
}

export function SetCurrentPagerData(data) { // New: Action creator for setting current pager data
    return async (dispatch) => { // dispatch is now an argument
        dispatch(slice.actions.setCurrentPagerData({
            data
        }))
    }
}

export function SetChatListTrigger() { // New: Action creator for triggering chat list refresh
    return async (dispatch) => {
        dispatch(slice.actions.setChatListTrigger());
    }
}

export function SetPagerListTrigger() { // New: Action creator for triggering pager list refresh
    return async (dispatch) => {
        dispatch(slice.actions.setPagerListTrigger());
    }
}

export function SetAllGroupChats(groupChats) { // New: Action creator for setting all raw group chats
    return async (dispatch) => {
        dispatch(slice.actions.setAllGroupChats({
            groupChats
        }));
    }
}

export function SetCurrentPatientDataForChat(patientData) { // New: Action creator for setting current patient data for chat
    return async (dispatch) => {
        dispatch(slice.actions.setCurrentPatientDataForChat(patientData)); // Dispatch the patientData object directly
    }
}

export function SetMessageToEdit(message) { // New: Action creator for setting message to edit
    return async (dispatch) => {
        dispatch(slice.actions.setMessageToEdit(message)); // Dispatch the message object directly
    }
}

export function ClearMessageToEdit() { // New: Action creator for clearing message to edit
    return async (dispatch) => {
        dispatch(slice.actions.clearMessageToEdit());
    }
}

export function SetMessageToDelete(message) { // New: Action creator for setting message to delete
    return async (dispatch) => {
        dispatch(slice.actions.setMessageToDelete(message));
    }
}

export function ClearMessageToDelete() { // New: Action creator for clearing message to delete
    return async (dispatch) => {
        dispatch(slice.actions.clearMessageToDelete());
    }
}

export function SetMessageToForward(message) { // New: Action creator for setting message to forward
    return async (dispatch) => {
        dispatch(slice.actions.setMessageToForward(message));
    }
}

export function ClearMessageToForward() { // New: Action creator for clearing message to forward
    return async (dispatch) => {
        dispatch(slice.actions.clearMessageToForward());
    }
}

export function SetCurrentGroupMembers(members) { // New: Action creator for setting current group members
    return async (dispatch) => {
        dispatch(slice.actions.setCurrentGroupMembers(members));
    }
}

export function SetIsChatStarting(isStarting) { // New: Action creator for controlling full-screen loader
    return async (dispatch) => {
        dispatch(slice.actions.setIsChatStarting(isStarting));
    }
}

export function SetNonAppChatEnded(isEnded) { // New: Action creator for setting non-app chat ended status
    return async (dispatch) => {
        dispatch(slice.actions.setNonAppChatEnded(isEnded));
    }
}

export function SetIsFirstMessageSentToNonAppUser(isFirst) { // New: Action creator for setting first message sent status
    return async (dispatch) => {
        dispatch(slice.actions.setIsFirstMessageSentToNonAppUser(isFirst));
    }
}

export function IncrementMessageBadge() {
    return async (dispatch) => {
        dispatch(slice.actions.incrementMessageBadge());
    };
}

export function ResetMessageBadge() {
    return async (dispatch) => {
        dispatch(slice.actions.resetMessageBadge());
    };
}

export function IncrementPagerBadge() {
    return async (dispatch) => {
        dispatch(slice.actions.incrementPagerBadge());
    };
}

export function ResetPagerBadge() {
    return async (dispatch) => {
        dispatch(slice.actions.resetPagerBadge());
    };
}

export function ResetChatState() { // New: Action creator for resetting all chat-related states
    return async (dispatch) => {
        dispatch(slice.actions.resetChatState());
    }
}